// Type-only for the same reason as the condition module: the console parses its
// cluster read through this file, and it must not drag the generated enum table
// into the browser bundle.
import type { DbEnum } from "../../../generated/prisma-enums";

/**
 * The parts of a tenant's dedicated cluster that its agent converges and reports
 * on one by one, each mirrored onto its own status column of the cluster row.
 *
 * 🔴 A COMPONENT IS A SEPARATE FACT FROM THE CLUSTER'S LIFECYCLE STATUS, AND THE
 * TWO DISAGREE IN PRODUCTION. A cluster can read `DEGRADED` for nothing but a
 * routine pending config diff while its git host answers 502 to every push: the
 * reconcile loop judges the stacks, the agent judges what runs on them. So every
 * surface that tells someone the cluster is fine — the customer's condition, the
 * operator's pager — has to read every component as well as the status, and this
 * registry is the one list of them.
 *
 * It is keyed so that a component added here is a compile error at every reader
 * until it is handled: the condition resolver's input, the customer copy below,
 * the backend's component detectors. And the backend census
 * (`every-cluster-component-is-ranked-and-paged.spec.ts`) holds this list to the
 * cluster row itself — every status column whose vocabulary carries `FAILED` must
 * be here — so a component column added to the schema and nowhere else reds.
 */
export const VIBE_CLUSTER_COMPONENT_VALUES = ["GIT_HOST", "TELEMETRY"] as const;

export type VibeClusterComponent = (typeof VIBE_CLUSTER_COMPONENT_VALUES)[number];

/** The cluster-row column each component's status is mirrored onto. */
export const VIBE_CLUSTER_COMPONENT_COLUMN = {
  GIT_HOST: "gitHostStatus",
  TELEMETRY: "telemetryStatus"
} as const satisfies Record<VibeClusterComponent, string>;

export type VibeClusterComponentColumn =
  (typeof VIBE_CLUSTER_COMPONENT_COLUMN)[VibeClusterComponent];

/**
 * Each component's status as the reader holds it: a value the contract lists, a
 * value a newer server sent that it does not (a branded string from a lenient
 * read), or null when the agent has never reported the component.
 */
export type VibeClusterComponentStatuses = Readonly<
  Record<VibeClusterComponentColumn, string | null>
>;

type GitHostStatusLiteral = (typeof DbEnum.VIBE_GIT_HOST_STATUS_VALUES)[number];
type TelemetryStatusLiteral = (typeof DbEnum.VIBE_TELEMETRY_STATUS_VALUES)[number];

/**
 * Whether each status a component can report is a FAULT — the component is not
 * doing its job and something the tenant relies on is refused.
 *
 * Exhaustive per component over its own database vocabulary, so a status added
 * to either enum (a `DEGRADED`, a `ROLLED_BACK`) is a compile error here until
 * somebody decides whether it is one. `PENDING` and `DEPLOYING` are a component
 * on its way up, which the cluster's lifecycle status already speaks for.
 */
const COMPONENT_STATUS_IS_FAULT: {
  readonly GIT_HOST: Readonly<Record<GitHostStatusLiteral, boolean>>;
  readonly TELEMETRY: Readonly<Record<TelemetryStatusLiteral, boolean>>;
} = {
  GIT_HOST: { PENDING: false, DEPLOYING: false, HEALTHY: false, FAILED: true },
  TELEMETRY: { PENDING: false, DEPLOYING: false, HEALTHY: false, FAILED: true }
};

/**
 * Whether each status a component can report is SERVING — the component has been
 * seen doing its job. Not the complement of a fault: `PENDING` and `DEPLOYING`
 * are neither faulted nor serving, a component on its way up.
 *
 * The backend's one road to a HEALTHY cluster reads this, so a cluster is never
 * called HEALTHY over a component nobody has yet seen work — the reconcile loop
 * once stamped a fresh tenant HEALTHY a minute before its git host was even
 * placed, and the row stayed HEALTHY while that git host never served.
 * Exhaustive per component over its own database vocabulary, like the fault map.
 */
const COMPONENT_STATUS_IS_SERVING: {
  readonly GIT_HOST: Readonly<Record<GitHostStatusLiteral, boolean>>;
  readonly TELEMETRY: Readonly<Record<TelemetryStatusLiteral, boolean>>;
} = {
  GIT_HOST: { PENDING: false, DEPLOYING: false, HEALTHY: true, FAILED: false },
  TELEMETRY: { PENDING: false, DEPLOYING: false, HEALTHY: true, FAILED: false }
};

/**
 * What the customer is told about a faulted component: its plain name, and what
 * it being down takes away from them. Written for the tenant, never the operator.
 */
export const VIBE_CLUSTER_COMPONENT_CUSTOMER_COPY: Readonly<
  Record<VibeClusterComponent, { readonly name: string; readonly blocked: string }>
> = {
  GIT_HOST: {
    name: "Your git host",
    blocked: "git push, clone and fetch fail, so no app can be deployed or updated"
  },
  TELEMETRY: {
    name: "Your log pipeline",
    blocked: "your apps' logs are not being collected"
  }
};

/**
 * Whether one reported status is a fault. A status this build does not list is
 * one: the only honest reading of a component state nobody can classify is that
 * it is not known to be working, and a surface that then says "nothing to do"
 * is the failure this registry exists to stop.
 */
export function isVibeClusterComponentFault(
  component: VibeClusterComponent,
  status: string | null
): boolean {
  if (status === null) return false;
  const verdicts: Readonly<Record<string, boolean>> = COMPONENT_STATUS_IS_FAULT[component];
  return Object.prototype.hasOwnProperty.call(verdicts, status) ? verdicts[status] === true : true;
}

/**
 * Whether one reported status is SERVING. Null is not — the agent has never
 * reported the component — and neither is a status this build does not list:
 * a state nobody can classify has not been seen working.
 */
export function isVibeClusterComponentServing(
  component: VibeClusterComponent,
  status: string | null
): boolean {
  if (status === null) return false;
  const verdicts: Readonly<Record<string, boolean>> = COMPONENT_STATUS_IS_SERVING[component];
  return Object.prototype.hasOwnProperty.call(verdicts, status) && verdicts[status] === true;
}

/** Every component whose reported status is not serving, in registry order. */
export function unservedVibeClusterComponents(
  statuses: VibeClusterComponentStatuses
): VibeClusterComponent[] {
  return VIBE_CLUSTER_COMPONENT_VALUES.filter(
    (component) =>
      !isVibeClusterComponentServing(component, statuses[VIBE_CLUSTER_COMPONENT_COLUMN[component]])
  );
}

/** Every component whose reported status is a fault, in registry order. */
export function faultedVibeClusterComponents(
  statuses: VibeClusterComponentStatuses
): VibeClusterComponent[] {
  return VIBE_CLUSTER_COMPONENT_VALUES.filter((component) =>
    isVibeClusterComponentFault(component, statuses[VIBE_CLUSTER_COMPONENT_COLUMN[component]])
  );
}
